import React from "react";
import io from "socket.io-client";
import Swal from "sweetalert2";
import { baseAxios, baseUrl } from "stores";

function chatTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function isImageAttachment(attachment) {
  return String(attachment?.mimeType || "").startsWith("image/");
}

function attachmentLabel(attachment) {
  const size = Number(attachment?.size || 0);
  const sizeText =
    size >= 1024 * 1024
      ? `${(size / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${attachment?.originalName || "Lampiran"} - ${sizeText}`;
}

function ChatAttachment({ attachment }) {
  if (!attachment?.url) return null;

  return (
    <a
      className={`chat-attachment ${isImageAttachment(attachment) ? "image" : ""}`}
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {isImageAttachment(attachment) ? (
        <img src={attachment.url} alt={attachment.originalName || "Lampiran"} />
      ) : (
        <i className="fas fa-file-alt" />
      )}
      <span>{attachmentLabel(attachment)}</span>
    </a>
  );
}

function validateChatAttachment(file) {
  if (!file) return true;
  if (file.size > 5 * 1024 * 1024) {
    Swal.fire("Lampiran terlalu besar", "Ukuran lampiran maksimal 5MB.", "warning");
    return false;
  }
  return true;
}

function mergeMessage(messages, message) {
  if (!message?.idChat) return messages;
  if (messages.some((item) => item.idChat === message.idChat)) return messages;
  return [...messages, message];
}

function upsertRoom(rooms, room) {
  if (!room?.idUser) return rooms;
  const next = rooms.filter((item) => item.idUser !== room.idUser);
  return [room, ...next].sort((a, b) => {
    const aTime = new Date(a.lastMessage?.dateChat || 0).getTime();
    const bTime = new Date(b.lastMessage?.dateChat || 0).getTime();
    return bTime - aTime;
  });
}

export default function AdminChat() {
  const [rooms, setRooms] = React.useState([]);
  const [selectedRoom, setSelectedRoom] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [attachment, setAttachment] = React.useState(null);
  const [loadingRooms, setLoadingRooms] = React.useState(true);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const listRef = React.useRef(null);
  const fileRef = React.useRef(null);

  const headers = React.useMemo(
    () => ({ token: localStorage.getItem("token") }),
    [],
  );

  const loadRooms = React.useCallback(async () => {
    setLoadingRooms(true);
    try {
      const response = await baseAxios.get("webChat/admin/rooms", { headers });
      setRooms(response.data.data || []);
    } catch (error) {
      Swal.fire("Gagal", error.response?.data?.message || "Gagal memuat chat", "error");
    } finally {
      setLoadingRooms(false);
    }
  }, [headers]);

  const loadMessages = React.useCallback(async (room) => {
    if (!room?.idUser) return;
    setSelectedRoom(room);
    setLoadingMessages(true);
    try {
      const response = await baseAxios.get(
        `webChat/admin/rooms/${room.idUser}/messages`,
        { headers },
      );
      setSelectedRoom(response.data.data.room);
      setMessages(response.data.data.messages || []);
      setRooms((current) => upsertRoom(current, response.data.data.room));
    } catch (error) {
      Swal.fire("Gagal", error.response?.data?.message || "Gagal memuat pesan", "error");
    } finally {
      setLoadingMessages(false);
    }
  }, [headers]);

  React.useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  React.useEffect(() => {
    const socket = io(baseUrl || window.location.origin);
    socket.emit("joinRoom", "admin-chat");
    socket.on("chat:message", (payload) => {
      if (payload?.room) {
        setRooms((current) => upsertRoom(current, payload.room));
      }
      if (payload?.message?.idUser === selectedRoom?.idUser) {
        setMessages((current) => mergeMessage(current, payload.message));
      }
    });
    socket.on("chat:read", (payload) => {
      if (payload?.room) {
        setRooms((current) => upsertRoom(current, payload.room));
      }
    });
    socket.on("chat:deleted", (payload) => {
      if (!payload?.idUser) return;
      setRooms((current) => current.filter((room) => room.idUser !== payload.idUser));
      if (payload.idUser === selectedRoom?.idUser) {
        setSelectedRoom(null);
        setMessages([]);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [selectedRoom?.idUser]);

  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  async function submit(event) {
    event.preventDefault();
    if (!selectedRoom?.idUser) return;
    if (!message.trim() && !attachment) {
      Swal.fire("Data belum lengkap", "Pesan atau lampiran wajib diisi.", "warning");
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("message", message || "");
      if (attachment) {
        formData.append("attachment", attachment);
      }
      const response = await baseAxios.post(
        `webChat/admin/rooms/${selectedRoom.idUser}/messages`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setMessages((current) => mergeMessage(current, response.data.data));
      setMessage("");
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (error) {
      Swal.fire("Gagal", error.response?.data?.message || "Pesan gagal dikirim", "error");
    } finally {
      setSending(false);
    }
  }

  function onAttachmentChange(event) {
    const file = event.target.files?.[0];
    if (!validateChatAttachment(file)) {
      event.target.value = "";
      setAttachment(null);
      return;
    }
    setAttachment(file || null);
  }

  async function deleteChat() {
    if (!selectedRoom?.idUser) return;

    const result = await Swal.fire({
      title: "Hapus chat?",
      text: `Seluruh riwayat chat dengan ${selectedRoom.nama} akan dihapus.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Hapus",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
    });

    if (!result.isConfirmed) return;

    try {
      await baseAxios.delete(`webChat/admin/rooms/${selectedRoom.idUser}`, {
        headers,
      });
      setRooms((current) => current.filter((room) => room.idUser !== selectedRoom.idUser));
      setSelectedRoom(null);
      setMessages([]);
      Swal.fire("Berhasil", "Chat berhasil dihapus.", "success");
    } catch (error) {
      Swal.fire("Gagal", error.response?.data?.message || "Chat gagal dihapus", "error");
    }
  }

  return (
    <div className="admin-chat-page">
      <div className="admin-chat-heading">
        <div>
          <span>Realtime</span>
          <h1>Chat User</h1>
        </div>
        <button onClick={loadRooms}>Refresh</button>
      </div>
      <div className="admin-chat-shell">
        <aside className="admin-chat-rooms">
          <div className="admin-chat-rooms-title">
            <strong>Percakapan</strong>
            <span>{rooms.length} room</span>
          </div>
          {loadingRooms ? (
            <div className="admin-chat-empty">Loading...</div>
          ) : rooms.length ? (
            rooms.map((room) => (
              <button
                className={selectedRoom?.idUser === room.idUser ? "active" : ""}
                key={room.idUser}
                onClick={() => loadMessages(room)}
              >
                <div>
                  <strong>{room.nama}</strong>
                  <span>{room.nopeg}</span>
                  <p>{room.lastMessage?.message || "-"}</p>
                </div>
                {room.unreadForAdmin > 0 && <b>{room.unreadForAdmin}</b>}
              </button>
            ))
          ) : (
            <div className="admin-chat-empty">Belum ada chat user.</div>
          )}
        </aside>
        <section className="admin-chat-conversation">
          {selectedRoom ? (
            <>
              <header>
                <div>
                  <strong>{selectedRoom.nama}</strong>
                  <span>{selectedRoom.nopeg} - {selectedRoom.email}</span>
                </div>
                <button className="admin-chat-delete" onClick={deleteChat}>
                  <i className="fas fa-trash" />
                  <span>Hapus chat</span>
                </button>
              </header>
              <div className="admin-chat-messages" ref={listRef}>
                {loadingMessages ? (
                  <div className="admin-chat-empty">Loading...</div>
                ) : messages.length ? (
                  messages.map((item) => (
                    <div
                      className={`admin-chat-bubble ${item.senderType === "admin" ? "mine" : "theirs"}`}
                      key={item.idChat}
                    >
                      <div>
                        <p>{item.message}</p>
                        {(item.attachments || []).map((file) => (
                          <ChatAttachment attachment={file} key={file.url || file.fileName} />
                        ))}
                        <span>{item.senderName || "-"} - {chatTime(item.dateChat)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="admin-chat-empty">Belum ada pesan.</div>
                )}
              </div>
              <form className="admin-chat-form" onSubmit={submit}>
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tulis balasan..."
                  maxLength="1000"
                  disabled={sending}
                />
                <label className="admin-chat-attach">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
                    onChange={onAttachmentChange}
                    disabled={sending}
                  />
                  <i className="fas fa-paperclip" />
                </label>
                <button disabled={sending}>
                  <i className="fas fa-paper-plane" />
                  <span>{sending ? "Mengirim..." : "Kirim"}</span>
                </button>
              </form>
              {attachment && (
                <div className="admin-chat-file-preview">
                  <span>{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachment(null);
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                  >
                    Hapus
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="admin-chat-placeholder">
              <i className="fas fa-comments" />
              <strong>Pilih percakapan</strong>
              <span>Chat dari user akan muncul di daftar sebelah kiri.</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
